/* global axios */
import Vue from 'vue';
import ApiClient from './ApiClient';

// Módulo Ventas de Chatsell. Desde el CRM el operador carga ventas y ve o
// anula las que él mismo cargó en el día: el listado completo y los totales
// son del dueño de la cuenta y viven en el panel.
class SalesAPI extends ApiClient {
  constructor() {
    super('sales', { accountScoped: true });
  }

  availability() {
    return axios.get(`${this.url}/availability`);
  }

  // `contactId` para un contacto del CRM; `customer` ({ name, phone }) para
  // alguien que no está cargado ahí.
  register(sale, { contactId = null, customer = null } = {}) {
    return axios.post(this.url, {
      sale,
      ...(contactId ? { contact_id: contactId } : {}),
      ...(!contactId && customer ? { customer } : {}),
    });
  }

  mine() {
    return axios.get(`${this.url}/mine`);
  }

  void(saleId) {
    return axios.post(`${this.url}/void`, { sale_id: saleId });
  }

  forContact(contactId) {
    return axios.get(`${this.url}/contact`, {
      params: { contact_id: contactId },
    });
  }

  buyers(contactIds) {
    return axios.post(`${this.url}/buyers`, { contact_ids: contactIds });
  }
}

const api = new SalesAPI();

// Quién ya compró, para la insignia de las tarjetas de la bandeja. Cada tarjeta
// pide su contacto y las pedidas de un mismo render salen juntas en una sola
// consulta. Lo sabido vale unos minutos; cargar o anular una venta lo olvida.
const BUYERS_TTL_MS = 5 * 60 * 1000;
const BUYERS_BATCH = 100;
const buyers = Vue.observable({ byKey: {}, generation: 0 });
let pending = {};
let flushTimer = null;

const buyerKey = (accountId, contactId) => `${accountId}:${contactId}`;

const flushBuyers = async () => {
  flushTimer = null;
  const batch = pending;
  pending = {};
  await Promise.all(
    Object.entries(batch).map(async ([accountId, ids]) => {
      const list = [...ids];
      for (let start = 0; start < list.length; start += BUYERS_BATCH) {
        const chunk = list.slice(start, start + BUYERS_BATCH);
        let found = null;
        try {
          // eslint-disable-next-line no-await-in-loop
          const { data } = await api.buyers(chunk);
          found = data?.buyers || {};
        } catch (error) {
          found = null;
        }
        const at = Date.now();
        chunk.forEach(id => {
          const key = buyerKey(accountId, id);
          // Si la consulta falla no se recuerda nada: la próxima vuelve a pedir.
          if (found === null) Vue.delete(buyers.byKey, key);
          else Vue.set(buyers.byKey, key, { at, value: found[id] || null });
        });
      }
    })
  );
};

export const buyerFor = (accountId, contactId) =>
  buyers.byKey[buyerKey(accountId, contactId)]?.value || null;

export const requestBuyer = (accountId, contactId) => {
  if (!accountId || !contactId) return;
  const known = buyers.byKey[buyerKey(accountId, contactId)];
  if (known && (known.loading || Date.now() - known.at < BUYERS_TTL_MS)) return;
  Vue.set(buyers.byKey, buyerKey(accountId, contactId), {
    ...known,
    loading: true,
  });
  pending[accountId] = pending[accountId] || new Set();
  pending[accountId].add(contactId);
  if (!flushTimer) flushTimer = setTimeout(flushBuyers, 50);
};

// Después de cargar o anular: el operador no siempre sabe de qué contacto era
// (cliente tipeado a mano), así que se olvida todo y cada tarjeta vuelve a pedir.
export const buyersGeneration = () => buyers.generation;

export const forgetBuyers = () => {
  buyers.byKey = {};
  buyers.generation += 1;
};

// El módulo se prende por cuenta desde el panel de Chatsell. Es reactivo para
// que el menú lateral muestre la entrada apenas llega la respuesta.
const access = Vue.observable({ byAccount: {} });

export const isSalesEnabledForAccount = accountId =>
  access.byAccount[accountId] === true;

export const clearSalesAccess = () => {
  access.byAccount = {};
};

// Se pregunta una vez por cuenta. Si la consulta falla la entrada no aparece y
// la falla no se recuerda: la próxima carga vuelve a preguntar.
export const loadSalesAccess = async accountId => {
  if (!accountId) return false;
  if (accountId in access.byAccount) return access.byAccount[accountId];
  try {
    const { data } = await api.availability();
    Vue.set(access.byAccount, accountId, data?.enabled === true);
    return access.byAccount[accountId];
  } catch (error) {
    return false;
  }
};

/**
 * "2.500,50", "250.000" o "2500.50" a "2500.50". Devuelve null si no es un
 * importe. Con coma, los puntos son miles. Sin coma, los puntos son miles solo
 * si arman grupos de tres ("250.000"); si no, el punto es el decimal ("2500.50").
 * La pantalla muestra el importe ya interpretado antes de guardar.
 */
export const parseAmount = text => {
  const clean = String(text || '')
    .trim()
    .replace(/\s|\$/g, '');
  let normal = clean;
  if (clean.includes(',')) normal = clean.replace(/\./g, '').replace(',', '.');
  else if (/^\d{1,3}(\.\d{3})+$/.test(clean)) normal = clean.replace(/\./g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(normal) || Number(normal) <= 0) return null;
  return normal;
};

export const formatAmount = (amount, currency = 'ARS') =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency }).format(
    Number(amount)
  );

// Totales por moneda ("$ 100.000 + US$ 50"): sumar pesos con dólares no da
// un número que signifique algo.
export const formatTotals = (totals = []) =>
  totals.map(total => formatAmount(total.amount, total.currency)).join(' + ');

// Día local como "2026-10-13": el valor de un <input type="date">.
export const localDay = (date = new Date()) => {
  const pad = value => String(value).padStart(2, '0');
  const month = pad(date.getMonth() + 1);
  return `${date.getFullYear()}-${month}-${pad(date.getDate())}`;
};

/**
 * Fecha de la venta a mandar. Hoy: ninguna, la hora la pone el servidor. Otro
 * día: el mediodía local, para que caiga en ese día en cualquier zona horaria.
 * Devuelve null si el día es futuro o no es una fecha.
 */
export const soldAtFor = (day, today = localDay()) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day || '') || day > today) return null;
  if (day === today) return undefined;
  const date = new Date(`${day}T12:00:00`);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

// `crypto.randomUUID` solo existe en https y navegadores recientes; sin él la
// pantalla no podría ni montarse. Lo único que importa es que no se repita.
export const operationId = () => {
  const { crypto } = window;
  if (crypto?.randomUUID) return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (crypto?.getRandomValues) crypto.getRandomValues(bytes);
  else
    bytes.forEach((_, i) => {
      bytes[i] = Math.floor(Math.random() * 256);
    });
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
};

export default api;
